import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X } from 'lucide-react';
import Logo from '../common/Logo';
import { trackEvent } from '../../utils/posthog';

const Header: React.FC = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const location = useLocation();

  const toggleMenu = () => setIsOpen(!isOpen);

  useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY > 10) {
        setIsScrolled(true);
      } else {
        setIsScrolled(false);
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Close mobile menu when route changes
  useEffect(() => {
    setIsOpen(false);
  }, [location]);

  const handleNavClick = (linkName: string) => {
    trackEvent('navigation_click', {
      link_name: linkName,
      location: 'header',
      path: location.pathname
    });
  };

  const handleCTAClick = (ctaType: string) => {
    trackEvent('cta_click', {
      cta_type: ctaType,
      location: 'header',
      path: location.pathname
    });
  };

  return (
    <header className={`sticky top-0 z-50 transition-all duration-200 ${
      isScrolled ? 'bg-white shadow-sm' : 'bg-transparent'
    }`}>
      <div className="container mx-auto px-4">
        <div className="flex justify-between items-center h-16">
          <div className="flex items-center">
            <Link to="/" className="flex items-center" onClick={() => handleNavClick('logo')}>
              <Logo />
            </Link>
          </div>
          
          {/* Desktop navigation */}
          <nav className="hidden md:flex items-center space-x-8">
            <Link 
              to="/" 
              className="text-gray-700 hover:text-[#7B4AE2] font-medium"
              onClick={() => handleNavClick('home')}
            >
              Home
            </Link>
            <Link 
              to="/how-it-works" 
              className="text-gray-700 hover:text-[#7B4AE2] font-medium"
              onClick={() => handleNavClick('how_it_works')}
            >
              How It Works
            </Link>
            <Link 
              to="/features" 
              className="text-gray-700 hover:text-[#7B4AE2] font-medium"
              onClick={() => handleNavClick('features')}
            >
              Features
            </Link>
            <Link 
              to="/pricing" 
              className="text-gray-700 hover:text-[#7B4AE2] font-medium"
              onClick={() => handleNavClick('pricing')}
            >
              Pricing
            </Link>
            <Link 
              to="/learn-more" 
              className="text-gray-700 hover:text-[#7B4AE2] font-medium"
              onClick={() => handleNavClick('learn_more')}
            >
              Learn More
            </Link>
          </nav>
          
          <div className="hidden md:flex items-center space-x-4">
            <Link 
              to="/login" 
              className="text-gray-700 hover:text-[#7B4AE2] font-medium"
              onClick={() => handleCTAClick('login')}
            >
              Log In
            </Link>
            <Link 
              to="/register" 
              className="bg-[#7B4AE2] text-white hover:bg-[#6A3ACF] px-4 py-2 rounded-md font-medium transition-colors"
              onClick={() => handleCTAClick('sign_up')}
            >
              Sign Up
            </Link>
          </div>
          
          {/* Mobile menu button */}
          <button 
            onClick={toggleMenu}
            className="md:hidden flex items-center"
            aria-label="Toggle menu"
          >
            {isOpen ? 
              <X className="h-6 w-6 text-gray-700" /> : 
              <Menu className="h-6 w-6 text-gray-700" />
            }
          </button>
        </div>
      </div>
      
      {/* Mobile navigation */}
      {isOpen && (
        <div className="md:hidden bg-white shadow-md">
          <div className="px-4 py-3 space-y-1">
            <Link 
              to="/" 
              className="block py-2 text-gray-700 hover:text-[#7B4AE2] font-medium"
              onClick={() => handleNavClick('home_mobile')}
            >
              Home
            </Link>
            <Link 
              to="/how-it-works" 
              className="block py-2 text-gray-700 hover:text-[#7B4AE2] font-medium"
              onClick={() => handleNavClick('how_it_works_mobile')}
            >
              How It Works
            </Link>
            <Link 
              to="/features" 
              className="block py-2 text-gray-700 hover:text-[#7B4AE2] font-medium"
              onClick={() => handleNavClick('features_mobile')}
            >
              Features
            </Link>
            <Link 
              to="/pricing" 
              className="block py-2 text-gray-700 hover:text-[#7B4AE2] font-medium"
              onClick={() => handleNavClick('pricing_mobile')}
            >
              Pricing
            </Link>
            <Link 
              to="/learn-more" 
              className="block py-2 text-gray-700 hover:text-[#7B4AE2] font-medium"
              onClick={() => handleNavClick('learn_more_mobile')}
            >
              Learn More
            </Link>
            <div className="border-t border-gray-200 my-2 pt-2">
              <Link 
                to="/login" 
                className="block py-2 text-gray-700 hover:text-[#7B4AE2] font-medium"
                onClick={() => handleCTAClick('login_mobile')}
              >
                Log In
              </Link>
              <Link 
                to="/register" 
                className="block py-2 text-[#7B4AE2] font-semibold"
                onClick={() => handleCTAClick('sign_up_mobile')}
              >
                Sign Up
              </Link>
            </div>
          </div>
        </div>
      )}
    </header>
  );
};

export default Header;